import React, { useState, useMemo } from 'react';
// No import needed for image generation, it's now handled by the server.

const Card = ({ id, children, background, isSummary }) => (
    <div
        id={id}
        className={`wrapped-card ${isSummary ? 'summary-card-final' : ''} ${background ? 'has-background' : ''}`}
        style={{
            backgroundImage: background
                ? `url(${background})`
                : 'none',
        }}
    >
        <div className="card-content">{children}</div>
    </div>
);


const SummaryCard = ({ data }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);

    const {
        totalHoursWatched,
        dailyAverageMinutes,
        totalUniqueShowsAndMovies,
        mostBingeDate,
        topGenres,
        // firstWatch,
        mostActiveDay,
        bingeStreak,
        totalMovies,
        totalShows,
        firstMovie,
        firstShow,
    } = data;

    const topGenresArray = useMemo(() => Object.entries(topGenres).sort(([, a], [, b]) => b - a).slice(0, 3), [topGenres]);

    const genrePhrase = useMemo(() => {
        if (topGenresArray.length === 0) return "You're an enigma!";
        const [first, second] = topGenresArray;
        const phrases = [
            `Mostly ${first[0]}, with a dash of ${second ? second[0] : `...well, more ${first[0]}!`}`,
            `You're all about the ${first[0]}, but you dabble in ${second ? second[0] : 'the classics'}.`,
            `${first[0]} is your jam, but you've got a soft spot for ${second ? second[0] : 'a good story'}.`,
            `Your world spins around ${first[0]}, with ${second ? second[0] : 'surprise twists'} on the side.`,
            `If it’s ${first[0]}, you’re in — though ${second ? second[0] : 'hidden gems'} sneak in too.`,
            `Nothing beats ${first[0]}, but ${second ? second[0] : 'a curveball pick'} keeps it fresh.`,
            `The spotlight’s on ${first[0]}, but ${second ? second[0] : 'wild cards'} share the stage.`,
            `You binge ${first[0]} like a pro, and ${second ? second[0] : 'random finds'} keep things spicy.`,
            `At heart you’re ${first[0]}, but ${second ? second[0] : 'offbeat vibes'} call your name sometimes.`,
            `Core identity: ${first[0]}. Secret indulgence: ${second ? second[0] : 'whatever feels right'}.`,
            ];
        return phrases[Math.floor(Math.random() * phrases.length)];
    }, [topGenresArray]);

    const getCleanTitle = (title) => {
        if (!title) return '';
        return title.split(':')[0].trim();
    };

    const cards = [
        <Card key="total-time" id="card-total-time">
            <div className="animated-content">
                <p className="eyebrow">You've spent an epic</p>
                <h2 className="headline">
                    {Math.floor(totalHoursWatched)} <span className="subtext">hrs</span> {Math.round((totalHoursWatched % 1) * 60)}{' '}
                    <span className="subtext">mins</span>
                </h2>
                <p className="description">lost in stories.</p>
            </div>
        </Card>,
        <Card key="daily-avg" id="card-daily-avg">
            <div className="animated-content">
                <p className="eyebrow">On average, you watched</p>
                <h2 className="headline">{Math.round(dailyAverageMinutes)} <span className="subtext">minutes</span></h2>
                <p className="description">every single day. That's commitment!</p>
            </div>
        </Card>,
        <Card key="total-titles" id="card-total-titles">
            <div className="animated-content">
                <p className="eyebrow">You journeyed through</p>
                <h2 className="headline">{totalUniqueShowsAndMovies}</h2>
                <p className="description">unique movies & shows.</p>
            </div>
        </Card>,
        <Card key="movies-shows" id="card-movies-shows">
            <p className="eyebrow">You watched</p>
            <div className="movie-show-container">
                <div className="stat-card movies">
                    <div className="stat-number-box">
                        <div className="stat-number">{totalMovies}</div>
                    </div>
                    <div className="stat-label-box">
                        <div className="stat-label">Movies</div>
                    </div>
                </div>
                <div className="stat-card shows">
                    <div className="stat-number-box">
                        <div className="stat-number">{totalShows}</div>
                    </div>
                    <div className="stat-label-box">
                        <div className="stat-label">Shows</div>
                    </div>
                </div>
            </div>
            <div className="first-watch-stack">
                {firstMovie && firstShow &&(
                    <p className="first-watch-text" style={{ animationDelay: '0.4s' }}>
                        Your first watched movie was <strong>{getCleanTitle(firstMovie.title)} </strong>
                        and your first watched show was <strong>{getCleanTitle(firstShow.title)}</strong>
                    </p>
                )}
            </div>
        </Card>,
        <Card key="top-genres" id="card-top-genres">
            <div className="animated-content">
                <p className="eyebrow">Your Top Genres</p>
            </div>
            <div className="top-genres-stack">
                {topGenresArray.map(([genre], index) => (
                    <div key={index} className="genre-item" style={{ animationDelay: `${index * 0.2}s` }}>
                        <div className="genre-rank-1">{index + 1}</div>
                        <h2 className="genre-name">{genre}</h2>
                    </div>
                ))}
            </div>
        </Card>,
        <Card key="binge-day" id="card-binge-day"  background={mostBingeDate.mostBingedSeries && mostBingeDate.mostBingedSeries.details ? `https://image.tmdb.org/t/p/w780${mostBingeDate.mostBingedSeries.details.poster_path}` : ''}>
            <div className="animated-content">
                <p className="eyebrow">Your most epic binge was on</p>
                <h2 className="headline">{new Date(mostBingeDate.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</h2>
                {mostBingeDate.mostBingedSeries && <p className="description">with a whopping {mostBingeDate.count} episodes of {mostBingeDate.mostBingedSeries.name}.</p>}
            </div>
        </Card>,
        <Card key="most-active-day" id="card-most-active-day">
            <div className="animated-content">
                <p className="eyebrow">Your Prime Time Was</p>
                <h2 className="headline">{mostActiveDay}</h2>
                <p className="description">the day you watched the most titles.</p>
            </div>
        </Card>,
        <Card key="binge-streak" id="card-binge-streak">
            <div className="animated-content">
                <p className="eyebrow">Longest Binge Streak</p>
                <h2 className="headline">{bingeStreak.length} <span className="subtext">days</span></h2>
                <p className="description">of consecutive watching.</p>
            </div>
        </Card>,

        // --- FINAL SUMMARY CARD WITH UPDATED LABELS ---
        <Card key="final-summary" id="card-final-summary" isSummary={true}>
            <h2 className="summary-title">NETFLIX RECAP</h2>
            <div className="summary-grid">
                <div className="summary-item">
                    <p className="summary-label">Time Well Spent</p>
                    <p className="summary-value">{Math.floor(totalHoursWatched)}h {Math.round((totalHoursWatched % 1) * 60)}m</p>
                </div>
                <div className="summary-item">
                    <p className="summary-label">Titles Explored</p>
                    <p className="summary-value">{totalUniqueShowsAndMovies}</p>
                </div>
                <div className="summary-item">
                    <p className="summary-label">Longest Streak</p>
                    <p className="summary-value">{bingeStreak.length} days</p>
                </div>
                <div className="summary-item">
                    <p className="summary-label">Most Active Day</p>
                    <p className="summary-value">{mostActiveDay}</p>
                </div>
            </div>
            <div className="summary-item full-width">
                <p className="summary-label">Your Vibe</p>
                <p className="summary-phrase">"{genrePhrase}"</p>
            </div>
            <div className="summary-item poster-item">
                {firstMovie && firstMovie.details && (
                    <div
                        className="poster"
                        style={{ backgroundImage: `url(https://image.tmdb.org/t/p/w500${firstMovie.details.poster_path})` }}
                    >
                        <span className="poster-label">Debut Watch (Movie)</span>
                    </div>
                )}
                {firstShow && firstShow.details && (
                    <div
                        className="poster"
                        style={{ backgroundImage: `url(https://image.tmdb.org/t/p/w500${firstShow.details.poster_path})` }}
                    >
                        <span className="poster-label">Debut Watch (Series)</span>
                    </div>
                )}
                {mostBingeDate.mostBingedSeries && (
                    <div
                        className="poster"
                        style={{ backgroundImage: `url(https://image.tmdb.org/t/p/w500${mostBingeDate.mostBingedSeries.details.poster_path})` }}
                    >
                        <span className="poster-label">All Time Favorite</span>
                    </div>
                )}
            </div>
            <p className="share-text">Share your #NetflixRecap</p>
        </Card>,
    ];

    // ... (handler functions remain the same)
    const handleNext = () => {
        if (currentIndex < cards.length - 1) setCurrentIndex(currentIndex + 1);
    };

    const handlePrev = () => {
        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        const node = document.getElementById('card-final-summary');

        try {
            // 1. Get the raw HTML of the card. The server will handle everything else.
            const html = node.outerHTML;

            // 2. Extract all active CSS from the document.
            let css = '';
            for (const sheet of Array.from(document.styleSheets)) {
                try {
                    const rules = sheet.cssRules || sheet.rules;
                    for (const rule of Array.from(rules)) {
                        css += rule.cssText;
                    }
                } catch (e) {
                    console.warn("Could not read stylesheet due to CORS.", sheet.href);
                }
            }

            // 3. Send the raw materials to the server.
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const fullUrl = `${apiUrl}/api/screenshot`;
            console.log('Requesting screenshot from:', fullUrl); // Log the URL

            const screenshotResponse = await fetch(fullUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ html, css }),
            });

            if (!screenshotResponse.ok) {
                const errorText = await screenshotResponse.text();
                throw new Error(`Server screenshot failed: ${screenshotResponse.statusText} - ${errorText}`);
            }

            // 4. Receive the final image and trigger the download.
            const imageBlob = await screenshotResponse.blob();
            const dataUrl = URL.createObjectURL(imageBlob);

            const link = document.createElement('a');
            link.download = 'netflix-recap.png';
            link.href = dataUrl;
            link.click();

            setTimeout(() => URL.revokeObjectURL(dataUrl), 100);

        } catch (error) {
            console.error('Error generating image via server:', error);
            alert('Sorry, there was an error creating your recap image. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="wrapped-container">
            <div className="carousel-wrapper">
                <div className="card-carousel">
                    {cards.map((card, index) => (
                        <div key={index} className={`carousel-item ${index === currentIndex ? 'active' : ''}`}>
                            {card}
                        </div>
                    ))}
                </div>
            </div>
            <div className="navigation">
                {currentIndex < cards.length - 1 ? (
                    <>
                        <button onClick={handlePrev} disabled={currentIndex === 0}>Prev</button>
                        <button onClick={handleNext}>Next</button>
                    </>
                ) : (
                    <>
                        <button onClick={handlePrev}>Back</button>
                        <button onClick={handleDownload} disabled={isDownloading}>
                            {isDownloading ? 'Downloading...' : 'Download'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SummaryCard;